import { apiCache } from '../utils/apiCache';
import { AxiosInstance } from 'axios';

interface RequestOptions {
  cache?: boolean;
  cacheTTL?: number;
  apiInstance?: AxiosInstance;
}

const DEFAULT_OPTIONS: RequestOptions = {
  cache: true,
  cacheTTL: 10000, // 10 seconds
};

export class BaseApi {
  private static pendingRequests: Map<string, Promise<any>> = new Map();

  static async get<T>(url: string, apiInstance: AxiosInstance, options: Partial<RequestOptions> = {}): Promise<T> {
    const finalOptions = { ...DEFAULT_OPTIONS, ...options };
    const cacheKey = url;
    
    // Check cache first if caching is enabled
    if (finalOptions.cache) {
      const cachedData = apiCache.get<T>(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    // If there's already a pending request for this URL, return that promise
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Make the actual request using the provided API instance
    const requestPromise = apiInstance.get(url)
      .then(response => response.data)
      .then(data => {
        // Cache the response if caching is enabled
        if (finalOptions.cache) {
          apiCache.set(cacheKey, data, finalOptions.cacheTTL);
        }
        // Remove from pending requests
        this.pendingRequests.delete(cacheKey);
        return data;
      })
      .catch(error => {
        // Remove from pending requests on error
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    // Store the pending request
    this.pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }
} 